import { useAtomValue } from "jotai";
import { useEffect } from "react";

import BottomDrawer from "components/BottomDrawer";
import ClientTranslation from "components/ClientTranslation";
import Flex from "components/Flex";
import { ErrorIcon } from "components/Icons/Error";
import { NoInternetIcon } from "components/Icons/NoInternet";
import Loader from "components/Loader";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { clientConfigAtom } from "atoms/clientConfig";
import { loaderTranslationKeyAtom } from "atoms/vehicleAnalysis";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useCompleteSession } from "hooks/useCompleteSession";
import { useEventTracking } from "hooks/useEventTracking";
import { usePageRouting } from "hooks/usePageRouting";

import { usePollDamagedParts } from "./hooks/usePollDamagedParts";
import { useStartPhotoClassification } from "./hooks/useStartPhotoClassification";
import { useUploadPhotos } from "./hooks/useUploadPhotos";

export const VehicleAnalysis = () => {
  const { onNext } = usePageRouting();
  const loaderTranslationKey = useAtomValue(loaderTranslationKeyAtom);

  const { damageReview } = useAtomValue(clientConfigAtom);
  const damageReviewDisabled = damageReview?.disabled;

  const { trackEvent } = useEventTracking();

  const { t } = useClientTranslation();

  // Handles image upload
  const { uploadAllImages, photoUploadError, photoUploadComplete } = useUploadPhotos();

  // Classification starts after image upload
  const { retryPhotoClassification, photoClassificationError, photosScanned } = useStartPhotoClassification();

  // Polling of damaged parts starts after image classification
  usePollDamagedParts();

  const { completeSession, completeSessionError } = useCompleteSession({
    onSuccess: () => {
      onNext();
    },
  });

  const retryFailedRequest = () => {
    if (photoUploadError) uploadAllImages();

    if (!!photoClassificationError) retryPhotoClassification();

    if (!!completeSessionError) completeSession();
  };

  useEffect(() => {
    if (!photoUploadComplete) {
      uploadAllImages();
    }
  }, [uploadAllImages, photoUploadComplete]);

  useEffect(() => {
    if (damageReviewDisabled && photosScanned) {
      completeSession();
    }
  }, [photosScanned, damageReviewDisabled, completeSession]);

  useEffect(() => {
    if (!navigator.onLine) {
      trackEvent({ name: "Offline" });
    }
  }, [trackEvent]);

  return (
    <PageLayout
      as="form"
      title={t("vehicle_analysis.header")}
      style={{ overflow: "hidden" }}
      dataTestId="vehicleAnalysis"
    >
      <Flex py={16} px={24} gap={16} style={{ height: "100%" }}>
        <Text sizeVariant="md">{t("vehicle_analysis.instruction")}</Text>
        <Text sizeVariant="md">
          <ClientTranslation i18nKey="vehicle_analysis.stay">{t("vehicle_analysis.stay")}</ClientTranslation>
        </Text>
        <Loader message={t(loaderTranslationKey)} />
      </Flex>
      <BottomDrawer
        isOpen={photoUploadError || !!photoClassificationError || !!completeSessionError}
        closeOnClickOutside={false}
        heading={navigator.onLine ? t("offline.error.header") : t("offline.header")}
        headingIcon={navigator.onLine ? <ErrorIcon /> : <NoInternetIcon />}
        callToActionText={t("offline.error.retry_uploading")}
        callToActionOnClick={retryFailedRequest}
      >
        <Text sizeVariant="md">{navigator.onLine ? t("offline.error.instruction") : t("offline.instruction")}</Text>
      </BottomDrawer>
    </PageLayout>
  );
};
